import { ShieldAlert, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Renders a calm modal when a listing save collides with an exclusive
 * Authority to Sell, or when it's a self-duplicate.
 *
 * `conflict = null | { kind: "EXCLUSIVE_ATS_CONFLICT", disputeId, conflictingListingId } | { kind: "DUPLICATE_LISTING" }`
 */
export function ConflictDialog({ conflict, onOpenChange }) {
  const open = !!conflict;
  const kind = conflict?.kind;

  const isExclusive = kind === "EXCLUSIVE_ATS_CONFLICT";
  const isSelfDup = kind === "DUPLICATE_LISTING";

  const disputeId = conflict?.disputeId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-gray-200 bg-white p-8 sm:p-10"
        data-testid="conflict-dialog"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
          {isExclusive ? (
            <ShieldAlert className="h-6 w-6 text-amber-500" />
          ) : (
            <Info className="h-6 w-6 text-amber-500" />
          )}
        </div>

        <DialogTitle
          className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900"
          data-testid="conflict-dialog-title"
        >
          {isExclusive && "This unit is under exclusive ATS"}
          {isSelfDup && "You've already listed this unit"}
        </DialogTitle>

        <DialogDescription
          className="mt-3 text-center text-sm leading-relaxed text-gray-600"
          data-testid="conflict-dialog-body"
        >
          {isExclusive && (
            <>
              An existing broker has filed an exclusive Authority to Sell for
              this property. A dispute has been logged
              {disputeId ? (
                <>
                  {" "}
                  (
                  <span className="font-semibold text-gray-800" data-testid="conflict-dispute-id">
                    #{disputeId.slice(0, 8)}
                  </span>
                  )
                </>
              ) : null}{" "}
              for admin review. You&apos;ll be notified once resolved.
            </>
          )}
          {isSelfDup && (
            <>
              This footprint (building + unit + city) already matches one of
              your Active listings. Archive the old one first, or double-check
              the unit number.
            </>
          )}
        </DialogDescription>

        <div className="mt-8 flex justify-center">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-8 text-sm font-semibold text-white hover:bg-[#14213A]"
            data-testid="conflict-understood-button"
          >
            Understood
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
